/* Widget-level actions: the download formats, and full screen.
 *
 * Download used to live only in the editor toolbar (Editor/index.js handleDownloadCSV),
 * which meant a published board had no way to export at all - the board toolbar had an
 * Export button but its handler had no branch for it. Every widget header needs the same
 * routine, so it lives here and the editor calls it too rather than keeping a second copy.
 *
 * ── FOUR FORMATS, ALL IN THE BROWSER ──
 * PDF, PNG, XLS and CSV are built here from data the page already holds - no server
 * endpoint is involved. (The API does carry /generate-pdf-report and
 * /generate-xlsx-report, but those re-fetch the source data server-side and can only
 * produce the whole board; what a reader wants is the widget in front of them, filtered
 * the way they filtered it.)
 *
 * PDF, PNG and XLS are always offered. CSV is the one format behind a switch - the
 * "csvdownload" widget/board property the editor writes - which is why only its caller
 * checks a flag.
 *
 * Every row, not every row ON SCREEN: the table paginates, so nothing here reads the
 * rendered DOM. Each format is written from the same array the table is drawing from,
 * after grouping and after data filters, so a file matches what the reader is looking at
 * and carries the rows below the fold with it.
 *
 * xlsx, jspdf and dom-to-image are heavy (~1MB together) and most sessions never export
 * anything, so all three are import()ed at the moment they are used. That is why the
 * format functions are async.
 */

/* Statically imported, unlike the three heavy libraries above: it is a small image that
   webpack turns into a URL (or an inline data URI), and every PDF and PNG needs it. */
import witmegLogo from "../assets/Images/witmeg-logo.png";
/* The namespace the extractor puts on the second and later periods' columns. Imported
   rather than spelled "__" here, so the writers and the reader cannot drift apart. */
import { PERIOD_SEPARATOR } from "./arrayHelper";
import moment from "moment";
/* The same pairing the filter band and the editor's preview use, so the dates named in
   a file's heading are grouped into the same periods the reader saw on screen. */
import { buildDatePeriods, periodFilterIds, periodName, readDateDisplay } from "./dateFilterDisplay";

/**
 * Serialize a cell value for CSV.
 * - Primitives → as-is
 * - Arrays of objects → "{ Key: Val; Key: Val } | { Key: Val; Key: Val }"
 * - Plain objects → "Key: Val; Key: Val"
 * - Arrays of primitives → pipe-joined
 */
export const serializeCellValue = (val) => {
  if (val === null || val === undefined) return "";

  // primitive
  if (typeof val !== "object") return String(val);

  // array
  if (Array.isArray(val)) {
    if (val.length === 0) return "";

    return val
      .map((item) => {
        if (item === null || item === undefined) return "";
        if (typeof item !== "object") return String(item);

        const itemEntries = Object.entries(item);
        return `{ ${itemEntries.map(([k, v]) => `${k}: ${serializeCellValue(v)}`).join("; ")} }`;
      })
      .join(" | ");
  }

  // plain object
  const entries = Object.entries(val);
  if (entries.length === 0) return "";

  return entries.map(([k, v]) => `${k}: ${serializeCellValue(v)}`).join("; ");
};

/* Every field is quoted and embedded quotes are doubled, so a value containing a comma
   or a line break does not shift the following columns. */
export const buildCsvString = (headers, rows) =>
  [
    headers.join(","),
    ...rows.map((row) =>
      headers
        .map((h) => `"${String(row[h] ?? "").replace(/"/g, '""')}"`)
        .join(","),
    ),
  ].join("\n");

/* The same quoting, straight off a header row and rows of cells - no intermediate object
   keyed by column name, so two columns sharing a display name both survive. */
export const buildCsvMatrixString = ({ headers, body }) =>
  [headers, ...body].map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");

export const triggerCsvDownload = (csvString, fileName) => {
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const stamp = () => new Date().toISOString().slice(0, 10);
// PNG names carry the time too - a chart exported twice in a day is two different pictures
const timeStamp = () => new Date().toISOString().slice(0, 19).replace("T", "_").replace(/:/g, "-");

/* A canvas may not be more than about 16k pixels a side, and a row is 26 of them, so one
   picture holds roughly 600 rows. A longer table is split across several images rather
   than refused - see exportRowsToPng. This caps how many: twenty images is around 12,000
   rows, past which someone is asking for a wall of pictures and means Excel or CSV. */
export const PNG_MAX_IMAGES = 20;

/* ── TWO DESCRIPTIONS OF A COLUMN, AND WE ARE HANDED EITHER ──
   The editor exports from a resource's `availableFields` - {fieldName, fieldDisplayName,
   checked}. A published widget exports from the list it built for the table itself, which
   is an Ant Design column - {title, dataIndex, key, fieldDisplayName} with no fieldName
   on it at all (BoardDataTableContainer builds it around line 106).

   Reading only `fieldName` was why an export off a board came out as the right number of
   columns with nothing in any of them: every header resolved to undefined, and every cell
   was looked up as row[undefined]. That is worth knowing about the CSV button too - it
   read the same way, so it had always written empty files from a board; the formats added
   alongside it only made it visible.

   Both shapes are answered here, once, so no format has to know which one it was given. */
const normalizeField = (field) => {
  if (!field) return null;
  if (typeof field === "string") return { key: field, header: field, checked: true, width: null };

  const key = field.fieldName || field.dataIndex || field.key;
  if (!key) return null;

  /* The width the author dragged this column to, in px - saved on the field by
     carryFieldWidths (redux/editorSlice.js) and read back here so an exported file has
     the same proportions as the table it is a copy of. Every format used to size its own
     columns from the content, so a report whose columns had been deliberately widened
     came out looking nothing like it. */
  const width = Number(field.width);

  return {
    // fieldDisplayName is what the author renamed the column to; title is Ant's own label
    header: field.fieldDisplayName || field.title || key,
    key,
    // an Ant column carries no `checked` - it is in the list because it is on screen
    checked: field.checked !== false,
    width: Number.isFinite(width) && width > 0 ? width : null,
    /* Set only by buildPeriodFields, and only on a comparison. Carried through so the
       formats that can draw a two-level header know which columns belong together;
       null everywhere else, which is what keeps an ordinary file unchanged. */
    periodLabel: field.periodLabel || null,
  };
};

const visibleFieldsOf = (fields) => (fields || []).map(normalizeField).filter((f) => f && f.checked);

/* ── ONE COLUMN SET, REPEATED PER PERIOD ───────────────────────────────────────
   A comparison report draws every column once per date range: Category, then period
   one's figures, then period two's. On screen ReportGrid builds that at render time
   and groups the repeats under a banner carrying each period's name. The exporters
   never saw it - they were handed the ORIGINAL field list, which describes period one
   only, so every period-two cell was looked up as row[undefined] and dropped. A file
   taken off a comparison report was silently half of it.

   This is that same expansion, applied to the field list instead of to Ant columns, so
   all four formats inherit it from buildExportMatrix without knowing periods exist.

   ── WHERE THE PERIOD'S NAME GOES ──
   Into the column header: "Last Week TotalSales". A sheet, a CSV and a picture have no
   shared way to draw a two-level banner, and a header that reads as a sentence survives
   being sorted, pivoted and pasted into an email in a way a merged cell above it does
   not. The banner proper is drawn by the formats that can - see writeSheet.

   ── THE KEY COLUMN APPEARS ONCE ──
   When the author set "Match rows by", that column identifies the row rather than
   measuring it, so repeating it per period would print the same category twice with
   nothing between them. It is lifted to the front, exactly as ReportGrid does.

   Returns the list untouched for an ordinary report, which is every report that existed
   before this: one period cannot be repeated. */
export const buildPeriodFields = ({ fields, periodKeys, periodLabels, matchBy }) => {
  const list = Array.isArray(fields) ? fields : [];
  const keys = Array.isArray(periodKeys) ? periodKeys : [];
  if (keys.length < 2 || !list.length) return list;

  // the two shapes normalizeField answers, read the same way here - see its note above
  const keyOf = (f) => f.fieldName || f.dataIndex || f.key;
  const headerOf = (f) => f.fieldDisplayName || f.title || keyOf(f);

  const keyColumn = matchBy ? list.find((f) => keyOf(f) === matchBy) : null;
  const base = keyColumn ? list.filter((f) => keyOf(f) !== matchBy) : list;

  const repeated = keys.reduce((all, key, periodIndex) => {
    const label = (periodLabels || {})[key] || key;

    return all.concat(
      base.map((field) => {
        const name = keyOf(field);
        /* Period one is never namespaced: whether a comparison comes back at all
           depends on what the reader filtered, and renaming period one's columns
           would make a report's own saved columns stop matching the moment a second
           range was added. See buildPeriodRows in utils/arrayHelper. */
        const column = periodIndex === 0 ? name : `${key}${PERIOD_SEPARATOR}${name}`;

        return {
          ...field,
          fieldName: column,
          dataIndex: column,
          key: column,
          title: column,
          fieldDisplayName: `${label} ${headerOf(field)}`,
          // which period this column belongs to, for the formats that draw a banner
          periodKey: key,
          periodLabel: label,
        };
      }),
    );
  }, []);

  return keyColumn ? [keyColumn, ...repeated] : repeated;
};

/* The one shape every format is written from: the header row, and the rows as strings,
   hidden columns already dropped. Built once here so a PDF, a sheet and a picture of the
   same widget can never disagree about what was in it. */
export const buildExportMatrix = ({ rows, fields }) => {
  const visible = visibleFieldsOf(fields);
  const dataRows = Array.isArray(rows) ? rows : [];
  if (!visible.length || !dataRows.length) return null;

  return {
    headers: visible.map((f) => f.header),
    body: dataRows.map((row) => visible.map((f) => serializeCellValue(row[f.key] ?? ""))),
    // the author's column widths in px, null where a column has none
    widths: visible.map((f) => f.width),
    /* The period each column belongs to, aligned with `headers`, or all nulls on an
       ordinary report. The formats that can draw a banner above the header row read
       this; the rest ignore it and keep the period's name in the header text. */
    periods: visible.map((f) => f.periodLabel),
  };
};

/* The banner above the header row, as [label, span] pairs, or null when this is not a
   comparison. Runs of the same period collapse into one cell; the key column at the
   front belongs to no period and spans alone with no label. */
export const buildPeriodBands = (periods) => {
  if (!Array.isArray(periods) || !periods.some(Boolean)) return null;

  return periods.reduce((bands, label, i) => {
    const previous = bands[bands.length - 1];
    // a run continues only while the label is the same AND is a real period
    if (i > 0 && previous && previous[0] === label && label) {
      previous[1] += 1;
      return bands;
    }
    bands.push([label || "", 1]);
    return bands;
  }, []);
};

/* ── A CHART'S NUMBERS, ONE TOTAL PER GROUP PER PERIOD ─────────────────────────
   The group-by field against the summed value field, which is what the chart plots -
   every format exports that rather than the rows underneath it.

   `periodKeys` is what makes this a comparison. With two or more, each group gets one
   total per period, read from the namespaced column the extractor built
   (`Result2__TotalSales`); the first period keeps the bare field name, exactly as
   arrayHelper lays the rows out. Without them it behaves as it always did.

   ── A MISSING FIGURE STAYS MISSING ──
   This used to total with `Number(row[valueField] || 0)`, which turns an absent value
   into a real zero. On a comparison that is a lie with a number attached: a category
   the first period never reported came out of the sheet as "0", indistinguishable from
   one that genuinely sold nothing - and this data has true zeros (TotalVat, VatRate) to
   tell it apart from. A group with nothing but absent values stays null, and the writers
   leave the cell blank, the same way the table shows a dash and the line chart a gap. */
const chartTotals = ({ rows, groupByField, valueField, periodKeys }) => {
  const dataRows = Array.isArray(rows) ? rows : rows?.Result || rows?.data || [];
  if (!groupByField || !valueField || !dataRows.length) return null;

  // one unnamed slot for an ordinary chart, one per response key for a comparison
  const keys = Array.isArray(periodKeys) && periodKeys.length > 1 ? periodKeys : [null];

  const order = [];
  const totals = {};

  dataRows.forEach((row) => {
    const group = row[groupByField];
    if (!(group in totals)) {
      order.push(group);
      totals[group] = keys.map(() => null);
    }

    keys.forEach((key, i) => {
      // period one is never namespaced - see buildPeriodRows in utils/arrayHelper
      const field = i === 0 || !key ? valueField : `${key}${PERIOD_SEPARATOR}${valueField}`;
      const value = row[field];
      if (value === null || value === undefined || value === "") return;
      totals[group][i] = (totals[group][i] === null ? 0 : totals[group][i]) + Number(value);
    });
  });

  /* Alphabetical, the same order the chart draws its axis in - see getValueArray in
     views/Dashboard/ReportWidjets/BoardChartContainer.js. This builds its own order
     from the rows, so without the sort a chart's CSV listed the categories in the
     API's order while the chart on screen listed them alphabetically, and the file
     stopped being a copy of the picture.

     Chart exports only. A TABLE's rows keep the order they are drawn in, which comes
     from buildExportMatrix, and nothing here touches that. */
  order.sort((a, b) => String(a).localeCompare(String(b)));

  return order.length ? { keys, order, totals } : null;
};

/* The single-period series, unchanged in shape: [{ name, value }]. */
export const buildChartSeries = ({ rows, groupByField, valueField }) => {
  const totals = chartTotals({ rows, groupByField, valueField });
  if (!totals) return null;
  return totals.order.map((name) => ({ name, value: totals.totals[name][0] }));
};

/* The header row and the body a chart's CSV and sheet are written from.

   One column for the group, then one per period carrying that period's NAME - the one
   the author typed in the Report Builder - so a file says "Last Week" rather than
   "Result" or, worse, nothing at all. An ordinary chart keeps its single value column
   headed by the field name, so nothing about those files changes. */
export const buildChartMatrix = ({ rows, groupByField, valueField, periodKeys, periodLabels }) => {
  const totals = chartTotals({ rows, groupByField, valueField, periodKeys });
  if (!totals) return null;

  const { keys, order } = totals;
  const multi = keys.length > 1;

  return {
    headers: [
      groupByField,
      ...keys.map((key) => (multi ? `${(periodLabels || {})[key] || key} ${valueField}` : valueField)),
    ],
    body: order.map((name) => [
      name,
      // null is "this period did not report it" - written blank, not zero
      ...totals.totals[name].map((value) => (value === null ? "" : value)),
    ]),
  };
};

/* What goes above the data in every format: what this is, where it came from, and which
   filters were applied when it was taken. Without the filters a reader cannot tell an
   export of everything from an export of one branch's figures.

   ── WHICH NAME HEADS THE FILE ──
   The board's name, with the widget's underneath it - the page header first, then which
   widget on the page this came from, in that order.

   On a report, and on a dashboard holding a single widget, those two are the same name:
   ReportWidjets.widgetTitleFor gives a lone widget the board's name, because the page
   header IS its title and it draws no header of its own. The second line is dropped
   rather than printing the name twice. */
/* Three tiers, not two. The board's name is the heading; the widget's name is a subtitle
   under it; the filters are the small print below both.

   The widget name used to be the first of `lines`, which meant it was drawn at the same
   size and colour as "Filters: Region equals North" - so on a dashboard the one thing
   saying WHICH table you were looking at was typeset as a footnote. It comes out as its
   own field so it can be given its own size.

   `subtitle` is empty on a report and on any one-widget board: there the widget's title IS
   the board's name (ReportWidjets.widgetTitleFor returns it), and printing it twice says
   nothing. */
export const buildHeadingLines = ({ boardName, widgetName, filters }) => {
  const title = boardName || widgetName || "Report";
  const subtitle = widgetName && widgetName !== title ? widgetName : "";
  const lines = [];

  /* ── NO "Filters:" LABEL ──
     Each line already names itself - "Period 1: 01 Aug 2026 - 31 Aug 2026", "Region:
     North". Heading the first of them with the word "Filters" added a category nobody
     needed and pushed the dates themselves into second place on the line, which is
     what a reader is actually looking for. The empty label is kept rather than the
     shape changed, because all three formats already draw a line with no label. */
  (filters || []).forEach((f) => lines.push(["", f]));

  return { title, subtitle, lines };
};

const buildFooterLines = ({ generatedBy }) => {
  const now = new Date();
  const lines = [["Generated", `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`]];
  if (generatedBy) lines.push(["Generated by", generatedBy]);
  return lines;
};

/* ── WHICH DATES THIS FILE IS OF ───────────────────────────────────────────────
   The GLOBAL filters in force, named for the heading block - the date ranges first,
   then anything else the reader set in the band above the widgets.

   Only the column filters were ever described here. The global ones are the filters
   that decide what the API was even asked for, so a comparison file arrived as two
   unlabelled blocks of figures with nothing saying what either period was, and an
   ordinary file gave no way to tell an export of September from an export of the year.
   On a printed sheet passed to somebody else that is unrecoverable.

   The ranges are named with the author's own period names - "Last Month", not
   "FromDate2" - read through the same pairing the filter band itself draws from, so
   the heading and the controls cannot disagree about which dates are which period.

   Returns [] when the report has no global filters at all, and the heading is then
   exactly what it was. */
const hasFilterValue = (value) => {
  if (value === null || value === undefined || value === "") return false;
  return !(Array.isArray(value) && value.length === 0);
};

const asExportDay = (value) => {
  if (!hasFilterValue(value)) return "";
  const at = moment(value);
  // a parameter that is not really a date is printed as it was typed rather than as
  // "Invalid date"
  return at.isValid() ? at.format("DD MMM YYYY") : String(value);
};

/* ── THE DATES THE RESOURCE ASKS FOR BY ITSELF ─────────────────────────────────
   A report whose reader has set no filters is NOT a report with no dates. The
   resource's own body parameters carry a From/To of their own - either a literal value
   typed in the Resource editor, or a rule like "14 days before today" resolved at
   request time - and those are the dates the API was actually asked for.

   Without this, an unfiltered export said nothing about its date range while the
   server-rendered version of the same report printed "From Date / To Date" plainly.
   The file could be of any month of the year.

   ── RESOLVED THE WAY THE REQUEST RESOLVES IT ──
   Mirrors ReportWidjets.fetchReport (search bodyParamObj): a literal `value` wins, and
   otherwise a `dateConfig` of "daysBefore" or "today" is computed. Those two are the
   only options the request itself honours - a resource set to "Fixed Date" is silently
   dropped there, and so is silently absent here, which at least keeps the heading
   honest about what was sent. If that resolver gains an option, this needs it too. */
const resolveDeclaredDate = (param) => {
  if (param.value) return param.value;

  const config = param.dateConfig;
  if (!config) return null;

  if (config.dateOption === "daysBefore") {
    return moment.utc().subtract(config.timeUnits, "day").startOf("day").format(config.dateFormat);
  }
  if (config.dateOption === "today") return moment().format(config.dateFormat);

  return null;
};

const declaredDateParams = (resource) => {
  /* Two shapes reach here: a board resource nests its connection twice, an editor
     resource once. Both are answered rather than assuming either. */
  const requestValues =
    resource?.connectionData?.connectionData?.requestValues ||
    resource?.connectionData?.requestValues ||
    resource?.source?.connectionData?.requestValues ||
    null;

  const params = requestValues?.bodyParams;
  if (!Array.isArray(params)) return [];

  return params
    .filter((p) => p && p.key && p.dataType === "date")
    .map((p) => ({ key: p.key, value: resolveDeclaredDate(p) }))
    .filter((p) => p.value);
};

// "FromDate" -> "From Date", matching the labels the filter band draws
const humanizeParamName = (name) => String(name || "").replace(/([a-z])([A-Z])/g, "$1 $2");

export const describeGlobalFilters = (resource) => {
  const filters = (resource?.customFilters || []).filter((f) => f && f.parameterName);

  /* A resource can declare dates without the report exposing a single filter, so this
     no longer gives up when there are no filters - it falls through to the declared
     parameters below. */
  const periods = buildDatePeriods(filters);
  const display = readDateDisplay(resource);
  const multiPeriod = periods.length > 1;
  const alreadyInRange = periodFilterIds(periods);

  const lines = [];

  periods.forEach((period, i) => {
    const from = asExportDay(period.from?.value);
    const to = asExportDay(period.to?.value);
    // a range nobody filled in says nothing worth printing
    if (!from && !to) return;
    // "any" rather than a blank, so a half-open range reads as deliberate
    lines.push(`${periodName(display, i, multiPeriod)}: ${from || "any"} – ${to || "any"}`);
  });

  filters.forEach((filter) => {
    if (alreadyInRange.has(filter.id)) return;
    if (!hasFilterValue(filter.value)) return;
    const value = Array.isArray(filter.value) ? filter.value.join(", ") : filter.value;
    lines.push(`${filter.parameterName}: ${value}`);
  });

  /* ── THE RESOURCE'S OWN DATES, WHERE NO FILTER REPLACED THEM ──
     A global filter with a value OVERRIDES the body parameter of the same name - see
     ReportWidjets.fetchReport, which writes bodyParamObj[parameterName] = value after
     the declared parameters are resolved. So a declared date is only worth printing
     when nothing overrode it; otherwise the heading would name both the date that was
     asked for and the one that replaced it.

     Printed one per line, From Date then To Date in the order the resource declares
     them, which is how the server-rendered report prints them. */
  const overridden = new Set(
    filters.filter((f) => hasFilterValue(f.value)).map((f) => String(f.parameterName)),
  );

  declaredDateParams(resource).forEach(({ key, value }) => {
    if (overridden.has(String(key))) return;
    lines.push(`${humanizeParamName(key)}: ${asExportDay(value)}`);
  });

  return lines;
};

/* Human-readable descriptions of the data filters in force, for the heading block.
   dataFilterEngine's own shapes - see utils/dataFilterEngine.js. */
export const describeDataFilters = (filters) =>
  (filters || [])
    .filter((f) => f && f.fieldName)
    .map((f) => {
      const field = f.fieldDisplayName || f.fieldName;
      const type = f.filterType || "equals";
      if (type === "between") return `${field}: ${f.value1 ?? ""} – ${f.value2 ?? ""}`;
      const value = Array.isArray(f.value) ? f.value.join(", ") : (f.value ?? "");
      return `${field}: ${type} ${value}`.trim();
    });

/* Everything below exports what the user configured, not the raw API payload: hidden
   columns are left out and headers use the display name. An export that did not match
   the table on screen would look like the wrong data. */

/**
 * Table widget → one row per record, one column per checked field.
 * Returns false when there was nothing to write, so the caller can say why.
 */
export const exportRowsToCsv = ({ rows, fields, widgetName }) => {
  /* Written from the same matrix as the other three formats, so a CSV and a PDF of one
     widget cannot disagree. It used to read the field list its own way - which is how it
     came to write files of empty columns off a board - and keyed its rows by header name,
     which silently dropped a column whenever two shared a display name. */
  const matrix = buildExportMatrix({ rows, fields });
  if (!matrix) return false;

  triggerCsvDownload(buildCsvMatrixString(matrix), `${widgetName || "report"}_${stamp()}.csv`);
  return true;
};

/**
 * Chart widget → the plotted series, not the underlying rows: the group-by field and the
 * summed value field, grouped exactly the way ChartContainer groups them for display.
 */
export const exportChartToCsv = ({
  rows,
  groupByField,
  valueField,
  widgetName,
  boardName,
  periodKeys,
  periodLabels,
}) => {
  /* Written from the matrix rather than from a {name, value} series keyed by header: a
     comparison has one value column PER PERIOD, and an object keyed by column name can
     only hold one of them. The sheet below is built the same way, so the two cannot
     disagree about how many periods a chart had. */
  const matrix = buildChartMatrix({ rows, groupByField, valueField, periodKeys, periodLabels });
  if (!matrix) return false;

  // the board's name when the chart has none of its own - see BoardChartContainer
  triggerCsvDownload(buildCsvMatrixString(matrix), `${widgetName || boardName || "chart"}_${stamp()}.csv`);
  return true;
};

/* ── LOADING THE LIBRARIES ──
   Each of these ships in a different shape - xlsx and downloadjs are CommonJS, jspdf
   exports jsPDF by name, jspdf-autotable a default function - and the interop wrapper
   webpack puts around a CommonJS module is not always the module itself. Each loader
   reaches for what it needs and falls back, so a bundler change cannot silently turn one
   of them into `undefined is not a function` at the moment someone clicks Download. */
const loadXlsx = async () => {
  const mod = await import("xlsx");
  return mod.utils ? mod : mod.default;
};

const loadJsPdf = async () => {
  const mod = await import("jspdf");
  return mod.jsPDF || mod.default;
};

const loadAutoTable = async () => {
  const mod = await import("jspdf-autotable");
  return mod.default || mod;
};

const loadDomToImage = async () => {
  const mod = await import("dom-to-image");
  return mod.toPng ? mod : mod.default;
};

const loadDownload = async () => {
  const mod = await import("downloadjs");
  return mod.default || mod;
};

/* ─────────────────────────────────────────────────────────────
   XLS
   One sheet: a heading block (board, widget, filters), the table under it, and a
   generated-on footer - so a sheet mailed on to someone says what it is a sheet of.
   XLSX.writeFile triggers the browser's own save, so there is no blob to clean up.
   ───────────────────────────────────────────────────────────── */
const writeSheet = async ({ headers, body, widths, periods, widgetName, boardName, filters, generatedBy }) => {
  const XLSX = await loadXlsx();

  const { title, subtitle, lines } = buildHeadingLines({ boardName, widgetName, filters });

  /* ── THE PERIOD BANNER ──
     A sheet is the one format that can draw the two-level header the table has on
     screen, because Excel merges cells. One row above the headers, each period's name
     spanning its own columns. Null on an ordinary report, and then nothing is added -
     the data block keeps its original row position for anyone with a saved formula
     pointing at it. */
  const bands = buildPeriodBands(periods);
  const bandRow = bands ? bands.reduce((row, [label, span]) => row.concat(label, ...Array(span - 1).fill("")), []) : null;

  // blank rows keep the heading and the footer off the table itself, so the data block
  // is still a clean rectangle to sort or pivot
  const aoa = [
    [title],
    // a sheet has no type scale to speak of, so the widget name is simply its own row
    ...(subtitle ? [[subtitle]] : []),
    // a blank row between the names and the filters, the sheet's version of the gap
    // the PDF and the picture open - see PDF_META_GAP
    ...(lines.length ? [[]] : []),
    ...lines.map(([label, value]) => (label ? [label, value] : [value])),
    [],
    ...(bandRow ? [bandRow] : []),
    headers,
    ...body,
    [],
    ...buildFooterLines({ generatedBy }),
  ];

  const sheet = XLSX.utils.aoa_to_sheet(aoa);

  /* Merged across each period's span. The row index is counted from the aoa above
     rather than assumed, so adding a heading line cannot silently move the merge onto
     the data. A single-column band is left unmerged - Excel rejects a merge of one. */
  if (bandRow) {
    const bandAt = aoa.indexOf(bandRow);
    let column = 0;
    sheet["!merges"] = bands.reduce((merges, [, span]) => {
      if (span > 1) merges.push({ s: { r: bandAt, c: column }, e: { r: bandAt, c: column + span - 1 } });
      column += span;
      return merges;
    }, []);
  }
  /* Column widths: the author's, where they set one. Excel measures in characters rather
     than pixels, and ~7px is one character at the default font.

     Otherwise the header, or the widest cell, whichever is longer - capped, or one long
     description stretches a column past the width of the screen. Folded rather than
     spread into Math.max: a 60,000-row export would spread 60,000 arguments into one
     call and overflow the stack. */
  sheet["!cols"] = headers.map((h, i) => {
    const px = widths?.[i];
    if (Number.isFinite(px) && px > 0) return { wch: Math.max(6, Math.min(120, Math.round(px / 7))) };

    return {
      wch: Math.min(
        60,
        body.reduce(
          (widest, row) => Math.max(widest, String(row[i] ?? "").length + 2),
          Math.max(10, String(h).length + 2),
        ),
      ),
    };
  });

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, "Report");
  XLSX.writeFile(book, `${widgetName || boardName || "report"}_${stamp()}.xlsx`);
  return true;
};

export const exportRowsToXlsx = async ({ rows, fields, widgetName, boardName, filters, generatedBy }) => {
  const matrix = buildExportMatrix({ rows, fields });
  if (!matrix) return false;
  return writeSheet({ ...matrix, widgetName, boardName, filters, generatedBy });
};

export const exportChartToXlsx = async ({
  rows,
  groupByField,
  valueField,
  widgetName,
  boardName,
  filters,
  generatedBy,
  periodKeys,
  periodLabels,
}) => {
  const matrix = buildChartMatrix({ rows, groupByField, valueField, periodKeys, periodLabels });
  if (!matrix) return false;
  return writeSheet({ ...matrix, widgetName, boardName, filters, generatedBy });
};

/* ─────────────────────────────────────────────────────────────
   PDF
   A table is drawn by autoTable from the same rows, NOT photographed: the text stays
   selectable and searchable, and the rows below the fold come with it. A chart has no
   text to draw, so it is photographed.
   ───────────────────────────────────────────────────────────── */
const pdfText = (value) => (value === null || value === undefined ? "" : String(value));

/* ── THE WITMEG MARK ─────────────────────────────────────────────────────────
   Every exported PDF and PNG carries it at the top right of the heading block, opposite
   the report's name. A file that leaves the app ends up in an inbox or a shared drive
   with nothing around it to say where it came from; the mark is what says so.

   Loaded as an <img> because that is what both destinations take - jsPDF's addImage and
   canvas's drawImage each accept one - so one loader serves both.

   Never rejects. A logo that will not load (a broken path after a build, an offline
   cache) must not cost somebody their export: the file is still written, just without
   the mark. */
const loadImage = (src) =>
  new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

/* Loaded once per session rather than per export. The promise itself is cached, so two
   exports started together share the one request instead of racing. */
let logoPromise = null;
const loadLogo = () => {
  if (!logoPromise) logoPromise = loadImage(witmegLogo);
  return logoPromise;
};

/* The mark's drawn width. Height follows from the file's own proportions, so replacing
   the asset with one of a different shape does not squash it. */
const LOGO_WIDTH_PT = 104; // PDF, in points
const LOGO_WIDTH_PX = 132; // PNG, in pixels

/* Never wider than a quarter of the block. On a narrow export - a two-column table - a
   fixed width would be most of the picture and would crowd the title off it. */
const LOGO_MAX_SHARE = 0.25;

const logoHeightFor = (logo, width) => (logo ? width * (logo.height / logo.width) : 0);

/* ── THE PDF HEADING BLOCK ──
   The report's name at the left, the mark at the right, and the two centred against each
   OTHER rather than both hung from the top. The mark is about twice the height of a
   one-line title, so top-aligning them left the name floating up by itself with the logo
   running a long way down past it. Centred, they read as one band.

   PDF_HEADING_TOP is where that band starts; everything else is measured from it. */
const PDF_HEADING_TOP = 26;
const PDF_HEADING_GAP = 16; // between the band and whatever is drawn under it

/* ── THE HEADING'S TYPE SCALE ──
   One place, shared by the PDF and the PNG (see PNG_TYPE below, which mirrors these
   numbers in pixels), so a report and a picture of the same report are typeset alike.

   24 bold for the board's name, 16 regular for the widget's, and the filters below both
   at 9. The title's colour is the brand blue the mark is drawn in, so the name and the
   logo either side of the band read as one piece rather than two. */
const PDF_TITLE_SIZE = 24;
const PDF_TITLE_RGB = [36, 90, 135]; // RGB(36, 90, 135)
/* The rule between cells. #e2e8f0, the grey the table's own column separators are
   drawn in on screen, so a PDF of a report is ruled the way the report is. */
const PDF_GRID_RGB = [226, 232, 240];
const PDF_SUBTITLE_SIZE = 16;
/* The filter lines. 11pt bold in slate, not 9pt grey: these say WHICH slice of the data
   the file holds - which dates, which branch - and at footnote size they read as small
   print nobody checks. Still well under the subtitle, so they sit below the names
   rather than competing with them. */
const PDF_META_SIZE = 11;
const PDF_META_RGB = [51, 65, 85];

/* Baseline-to-baseline, each a little over its type size so descenders have room. */
const PDF_TITLE_LINE = 30;
const PDF_SUBTITLE_LINE = 21;
const PDF_META_LINE = 16;
/* Air between the names and the filters, so the block reads as two things - what this
   is, then what it is of - instead of one list run together. */
const PDF_META_GAP = 10;

/* The band's dimensions, worked out WITHOUT a document.

   Split from the drawing so a caller can know how tall the heading will be before the
   page exists - which is what buildNodePdf needs, because it sizes the page to its chart
   and cannot do that until it knows how much of the page the heading will take. Sharing
   one measurement means the space reserved and the space drawn into cannot drift. */
const pdfHeadingMetrics = ({ boardName, widgetName, filters, logo, pageWidth }) => {
  const { title, subtitle, lines } = buildHeadingLines({ boardName, widgetName, filters });

  const logoWidth = logo ? Math.min(LOGO_WIDTH_PT, (pageWidth - PDF_MARGIN * 2) * LOGO_MAX_SHARE) : 0;
  const logoHeight = logoHeightFor(logo, logoWidth);

  /* Measured rather than assumed, so the band grows with a subtitle or with the number of
     filters instead of letting either overflow into the table. */
  const textHeight =
    PDF_TITLE_LINE +
    (subtitle ? PDF_SUBTITLE_LINE : 0) +
    (lines.length ? PDF_META_GAP : 0) +
    lines.length * PDF_META_LINE;
  const blockHeight = Math.max(logoHeight, textHeight);

  return {
    title,
    subtitle,
    lines,
    logoWidth,
    logoHeight,
    textHeight,
    blockHeight,
    // where the next thing on the page may start
    bottom: PDF_HEADING_TOP + blockHeight + PDF_HEADING_GAP,
  };
};

const drawPdfHeading = (doc, { boardName, widgetName, filters, logo }) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const { title, subtitle, lines, logoWidth, logoHeight, textHeight, blockHeight } =
    pdfHeadingMetrics({ boardName, widgetName, filters, logo, pageWidth });

  if (logo) {
    try {
      doc.addImage(
        logo,
        "PNG",
        pageWidth - PDF_MARGIN - logoWidth,
        PDF_HEADING_TOP + (blockHeight - logoHeight) / 2,
        logoWidth,
        logoHeight,
      );
    } catch (e) {
      /* jsPDF throws rather than returns on an image it cannot read. The heading and the
         table matter more than the mark, so this is swallowed - the export continues
         without it. */
      // eslint-disable-next-line no-console
      console.warn("[export] the logo could not be drawn into the PDF", e);
    }
  }

  // doc.text draws from the BASELINE, so the first line sits a full title size down
  let y = PDF_HEADING_TOP + (blockHeight - textHeight) / 2 + PDF_TITLE_SIZE;

  doc.setFont(undefined, "bold");
  doc.setFontSize(PDF_TITLE_SIZE);
  doc.setTextColor(...PDF_TITLE_RGB);
  doc.text(pdfText(title), PDF_MARGIN, y);

  /* Back to regular before anything else is drawn - jsPDF's font weight is document
     state, not per-call, so leaving it bold would carry into the subtitle, the filters
     and the table below. */
  doc.setFont(undefined, "normal");

  if (subtitle) {
    y += PDF_SUBTITLE_LINE;
    doc.setFontSize(PDF_SUBTITLE_SIZE);
    doc.setTextColor(71, 85, 105);
    doc.text(pdfText(subtitle), PDF_MARGIN, y);
  }

  if (lines.length) {
    // the gap reserved in pdfHeadingMetrics, opened here - the two must stay in step
    y += PDF_META_GAP;
    doc.setFont(undefined, "bold");
    doc.setFontSize(PDF_META_SIZE);
    doc.setTextColor(...PDF_META_RGB);
    lines.forEach((line) => {
      y += PDF_META_LINE;
      doc.text(`${line[0] ? `${line[0]}: ` : ""}${pdfText(line[1])}`, PDF_MARGIN, y);
    });
    // jsPDF's weight is document state - the table below must not inherit bold
    doc.setFont(undefined, "normal");
  }

  return PDF_HEADING_TOP + blockHeight + PDF_HEADING_GAP;
};

const drawPdfFooter = (doc, y, { generatedBy }) => {
  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  buildFooterLines({ generatedBy }).forEach((line, i) => {
    doc.text(`${line[0]}: ${pdfText(line[1])}`, 40, y + 16 + i * 12);
  });
};

/* ── HOW WIDE THE PAGE IS ──
   A table of forty columns fits on no standard paper size, and there are only two ways
   to deal with that:

     1. break the columns across pages - the reader gets columns 1-9 on page one and the
        rest stranded in a block further down, and has to hold one row together in their
        head across pages
     2. make the page as wide as the table needs, and let the reader zoom

   This does (2). Keeping a row on one line is the whole point of a table, and zooming is
   something every PDF reader does well. (1) is what autoTable's horizontalPageBreak
   gives, and it is what this used to do.

   PDF allows a page up to 14400pt a side, so that is the ceiling; past it every column
   is scaled by the same factor, which keeps the author's proportions. */
const PDF_MAX_PAGE_PT = 14000;
const PT_PER_PX = 0.75;
const DEFAULT_COL_PX = 130;
const PDF_MARGIN = 40;

const pdfColumnWidths = (headers, widths) => {
  const raw = headers.map((_h, i) => {
    const px = widths?.[i];
    return (Number.isFinite(px) && px > 0 ? px : DEFAULT_COL_PX) * PT_PER_PX;
  });

  const total = raw.reduce((sum, w) => sum + w, 0);
  const room = PDF_MAX_PAGE_PT - PDF_MARGIN * 2;
  if (total <= room) return raw;

  const scale = room / total;
  return raw.map((w) => w * scale);
};

/* ── FILLING THE PAGE ──────────────────────────────────────────────────────────
   Every column scaled by the same factor so the set spans the page exactly.

   There was a branch for a table WIDER than the page - grow the paper, see
   PDF_MAX_PAGE_PT - and none for a narrower one, so a five-column report came out as
   a block across about two thirds of an A4 sheet with a third of it blank. Each column
   was sitting at its default 130px and `tableWidth: "wrap"` forbids autoTable from
   stretching them, which is what keeps the author's dragged widths intact.

   Scaling preserves those proportions exactly - a column the author made twice as wide
   as its neighbour still is - while using the whole page. A wide table is unaffected:
   the page was already grown to fit it, so the factor comes out as 1. */
const fillPageWidth = (columns, pageWidth) => {
  const total = columns.reduce((sum, w) => sum + w, 0);
  const room = pageWidth - PDF_MARGIN * 2;
  if (!total || total >= room) return columns;

  const scale = room / total;
  return columns.map((w) => w * scale);
};

/* ── ONE RENDERER, TWO DESTINATIONS ──
   Builds the document and does nothing with it, so the same bytes can be saved to disk
   (exportRowsToPdf, below) or attached to an email (buildRowsPdfAttachment).

   Extracted rather than copied. A second renderer for the emailed copy would be a second
   thing to keep in step, and the first time the two drifted a recipient would get a file
   that did not match the one the sender downloaded - which is impossible to debug from a
   support ticket. There is one renderer, so they cannot disagree.

   Returns the doc together with the name it would be saved under, or null when there is
   nothing to draw. */
const buildRowsPdf = async ({ rows, fields, widgetName, boardName, filters, generatedBy }) => {
  const matrix = buildExportMatrix({ rows, fields });
  if (!matrix) return null;

  const [jsPDF, autoTable, logo] = await Promise.all([loadJsPdf(), loadAutoTable(), loadLogo()]);

  const natural = pdfColumnWidths(matrix.headers, matrix.widths);
  const tableWidth = natural.reduce((sum, w) => sum + w, 0);

  /* The page is sized to the table rather than the table squeezed into the page. Height
     stays A4 landscape - rows still paginate downwards, which reads normally. */
  const pageWidth = Math.max(842, Math.ceil(tableWidth + PDF_MARGIN * 2));
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: [pageWidth, 595] });

  // and then spread across whatever page that turned out to be - see fillPageWidth
  const columns = fillPageWidth(natural, pageWidth);

  const startY = drawPdfHeading(doc, { boardName, widgetName, filters, logo });

  /* Each column pinned to its computed width. tableWidth "wrap" stops autoTable
     stretching them on its own terms, which would undo the author's proportions. */
  const columnStyles = {};
  columns.forEach((w, i) => {
    columnStyles[i] = { cellWidth: w };
  });

  /* ── THE PERIOD BANNER ──
     autoTable takes a head of several rows and honours colSpan, so the PDF gets the
     same two-level header the table has on screen rather than only the period names
     folded into each column. Absent on an ordinary report, where head stays one row. */
  const bands = buildPeriodBands(matrix.periods);
  const head = bands
    ? [bands.map(([label, span]) => ({ content: label, colSpan: span, styles: { halign: "center" } })), matrix.headers]
    : [matrix.headers];

  autoTable(doc, {
    head,
    body: matrix.body,
    startY,
    margin: { left: PDF_MARGIN, right: PDF_MARGIN },
    tableWidth: "wrap",
    columnStyles,
    /* ── EVERY CELL BOUNDED ──
       autoTable's default "striped" theme draws horizontal rules only, so a row read
       as one continuous strip of numbers and a reader had to count across to work out
       which figure belonged to which column - worst on a comparison, where the same
       four column names appear twice. "grid" rules every cell on all four sides, which
       is how the table is drawn on screen and in the server-rendered report. */
    theme: "grid",
    styles: {
      overflow: "linebreak",
      fontSize: 8,
      cellPadding: 4,
      // the same #e2e8f0 the grid uses for its column separators
      lineColor: PDF_GRID_RGB,
      lineWidth: 0.5,
    },
    /* The brand blue the title and the mark are drawn in, rather than the near-black
       navy this used to be: one blue down the page, and it matches the report the
       server generates. Separated by white rules so the header reads as cells too. */
    headStyles: {
      fillColor: PDF_TITLE_RGB,
      textColor: 255,
      fontStyle: "bold",
      halign: "left",
      lineColor: [255, 255, 255],
      lineWidth: 0.5,
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
  });

  drawPdfFooter(doc, doc.lastAutoTable?.finalY || startY, { generatedBy });
  return { doc, fileName: `${widgetName || boardName || "report"}_${stamp()}.pdf` };
};

/* The Download button's PDF. Unchanged in name, arguments and return value - it answers
   false rather than throwing when there is nothing to write, which is what DownloadMenu
   reports on. */
export const exportRowsToPdf = async (context) => {
  const built = await buildRowsPdf(context);
  if (!built) return false;

  built.doc.save(built.fileName);
  return true;
};

/**
 * The same PDF, base64-encoded, for the Email dialog to attach.
 *
 * Returns { fileName, fileContent } - the field names the email registry's Attachments[]
 * entry uses - or null when the report has no rows to draw.
 *
 * ── WHY datauristring AND NOT arraybuffer ──
 * The obvious route is doc.output("arraybuffer") and then base64-encode it, but the usual
 * one-liner for that - btoa(String.fromCharCode(...bytes)) - spreads every byte as a
 * separate argument and overflows the call stack somewhere around a few hundred KB. A
 * report PDF is comfortably past that. jsPDF already produces a correct base64 string
 * internally for its data URI, so this takes that and drops the
 * "data:application/pdf;filename=...;base64," prefix rather than re-encoding by hand.
 */
export const buildRowsPdfAttachment = async (context) => {
  const built = await buildRowsPdf(context);
  if (!built) return null;

  const dataUri = built.doc.output("datauristring");
  const marker = "base64,";
  const at = dataUri.indexOf(marker);
  if (at === -1) return null;

  return {
    fileName: built.fileName,
    fileType: "pdf",
    fileContent: dataUri.slice(at + marker.length),
  };
};

/* ── CROPPING THE BLANK BORDER OFF A CAPTURE ──
   Returns { dataUrl, width, height } for the picture with its white margin removed, or
   the original untouched when there is nothing to trim.

   A widget is mostly padding - ApexCharts' own inset plus the widget's - so a capture is
   a small drawing in the middle of a large white rectangle. Scaling THAT to fit a page
   fills the page with the padding. Cropping first means the chart itself is what gets
   enlarged.

   Anything within TRIM_WHITE_AT of pure white counts as blank: the background is #fff but
   antialiasing along the edge of the drawing leaves pixels a shade or two off it, and an
   exact-white test would stop at the first of those and trim almost nothing.

   Never throws. A capture that cannot be read back is returned as it came in - a chart
   with its margins intact is far better than no chart. */
const TRIM_WHITE_AT = 250; // 0-255 per channel
const TRIM_PAD_PX = 8; // a little air left around the drawing, so it is not cut flush

/* Only a run this wide is treated as a void worth closing. Below it the blank is ordinary
   spacing - between a donut and its labels, or either side of an axis title - and closing
   it would crush the drawing together. */
const COLLAPSE_MIN_PX = 48;
const COLLAPSE_TO_PX = 28; // what such a run becomes: a deliberate gap, not a seam

const isBlankPixel = (data, i) =>
  data[i + 3] < 8 ||
  (data[i] >= TRIM_WHITE_AT && data[i + 1] >= TRIM_WHITE_AT && data[i + 2] >= TRIM_WHITE_AT);

/* The x-ranges worth keeping: the drawing split at every wide blank run inside it.
   Returns one segment when there is no void, which is the common case. */
const blankColumnSegments = (data, canvasWidth, { left, right, top, bottom }) => {
  const blank = [];
  for (let x = left; x <= right; x += 1) {
    let empty = true;
    for (let y = top; y <= bottom; y += 1) {
      if (!isBlankPixel(data, (y * canvasWidth + x) * 4)) {
        empty = false;
        break;
      }
    }
    blank.push(empty);
  }

  const segments = [];
  /* Starts at 0 rather than at the first ink, so the TRIM_PAD_PX breathing space added to
     `left` survives into the first segment instead of being scanned away again. */
  let start = 0;
  let run = 0;

  for (let i = 0; i < blank.length; i += 1) {
    if (blank[i]) {
      run += 1;
      continue;
    }
    /* A run long enough to be a void closes the segment before it; a shorter one is
       ordinary spacing and is kept as part of the drawing. */
    if (run >= COLLAPSE_MIN_PX) {
      const end = i - run; // exclusive
      if (end > start) segments.push({ from: left + start, width: end - start });
      start = i;
    }
    run = 0;
  }

  if (blank.length > start) segments.push({ from: left + start, width: blank.length - start });

  return segments.length ? segments : [{ from: left, width: right - left + 1 }];
};

const trimImageBorder = async (dataUrl) => {
  const image = await loadImage(dataUrl);
  if (!image || !image.width || !image.height) return null;

  const untrimmed = { dataUrl, width: image.width, height: image.height };

  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return untrimmed;

    canvas.width = image.width;
    canvas.height = image.height;
    ctx.drawImage(image, 0, 0);

    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);

    let top = canvas.height;
    let left = canvas.width;
    let right = -1;
    let bottom = -1;

    for (let y = 0; y < canvas.height; y += 1) {
      for (let x = 0; x < canvas.width; x += 1) {
        if (isBlankPixel(data, (y * canvas.width + x) * 4)) continue;
        if (y < top) top = y;
        if (y > bottom) bottom = y;
        if (x < left) left = x;
        if (x > right) right = x;
      }
    }

    // an entirely blank capture - nothing to crop to, so leave it alone
    if (right < left || bottom < top) return untrimmed;

    left = Math.max(0, left - TRIM_PAD_PX);
    top = Math.max(0, top - TRIM_PAD_PX);
    right = Math.min(canvas.width - 1, right + TRIM_PAD_PX);
    bottom = Math.min(canvas.height - 1, bottom + TRIM_PAD_PX);

    const height = bottom - top + 1;

    /* ── THE GAP IN THE MIDDLE ──
       Cropping the border is not enough for a donut. Its legend is pinned to the right of
       the widget (chartOptions.buildDonutChartOptions, legend.position "right"), so
       ApexCharts reserves that whole side and a wide widget leaves a lake of white
       BETWEEN the donut and the legend. That is interior, so no amount of border
       trimming reaches it, and the drawing lands on the page looking shoved to the left.

       So any run of entirely blank columns inside the crop is squeezed down to a fixed
       gap, which pulls the legend in beside the donut and leaves the pair sitting in the
       middle of the page.

       A cartesian chart is untouched by this, and not by luck: its gridlines run the full
       width, so no column inside the plot is ever completely blank and there is no run to
       find. Only a chart with a genuine void - a donut beside its legend - has one. */
    const segments = blankColumnSegments(data, canvas.width, { left, right, top, bottom });

    const width = segments.reduce((sum, s) => sum + s.width, 0) + COLLAPSE_TO_PX * (segments.length - 1);
    if (width === canvas.width && height === canvas.height) return untrimmed;

    const out = document.createElement("canvas");
    const outCtx = out.getContext("2d");
    if (!outCtx) return untrimmed;

    out.width = width;
    out.height = height;
    // the page behind it is white, and a transparent crop prints as black in some readers
    outCtx.fillStyle = "#ffffff";
    outCtx.fillRect(0, 0, width, height);

    let cursor = 0;
    segments.forEach((segment, i) => {
      if (i) cursor += COLLAPSE_TO_PX;
      outCtx.drawImage(canvas, segment.from, top, segment.width, height, cursor, 0, segment.width, height);
      cursor += segment.width;
    });

    return { dataUrl: out.toDataURL("image/png"), width, height };
  } catch (e) {
    /* getImageData throws on a tainted canvas. Data URLs are same-origin so this should
       not happen, but a chart drawn with a cross-origin image would trip it. */
    // eslint-disable-next-line no-console
    console.warn("[export] the chart's border could not be trimmed", e);
    return untrimmed;
  }
};

/* A chart: photograph it, then place the picture on a page sized to its own proportions
   so it is not letterboxed inside a shape it was never drawn for.

   Split the same way as buildRowsPdf above, and for the same reason - the Download button
   saves it, the Email dialog attaches it, and both go through this one renderer so the
   file a recipient opens is the file the sender would have downloaded. */
/* ── HOW BIG THE CHART'S PAGE IS ──
   Sized to the chart, not the chart squeezed into a page - the same choice the table PDF
   makes (see PDF_MAX_PAGE_PT above), and for the same reason.

   A chart node is typically wide and short: a widget maybe 1000x400. Fitted into A4
   landscape it filled the width and used well under half the height, so every chart PDF
   came out as a band across the top of a mostly empty sheet. The page is now as tall as
   the chart needs and no taller.

   The width stays A4 landscape so the files are a familiar size to print and to read;
   only the height follows the chart. */
const CHART_PDF_PAGE = [842, 595]; // A4 landscape, in points
const CHART_PDF_FOOTER = 52; // room for the two generated-on lines under the chart

const buildNodePdf = async ({ node, widgetName, boardName, filters, generatedBy }) => {
  if (!node) return null;

  if (!(node.scrollWidth || node.offsetWidth) || !(node.scrollHeight || node.offsetHeight)) return null;

  // same guarded capture as the PNG - a stub data URL would land in the page as a blank box
  const [jsPDF, captured, logo] = await Promise.all([loadJsPdf(), captureNode(node), loadLogo()]);
  if (!captured) return null;

  /* ── TRIMMED BEFORE IT IS PLACED ──
     A chart widget is mostly padding: ApexCharts leaves a generous margin inside its own
     box, and the widget adds its own around that. Placed as captured, the picture is
     scaled to fit the page - so the page is filled by the PADDING, and the chart itself
     ends up small in the middle of it with white all around.

     Trimming the blank border first means the scaling works on the drawing rather than on
     the box it was sitting in, which is what actually makes the chart bigger on the page.
     Falls back to the untrimmed capture if there is nothing to trim or the scan fails. */
  const image = await trimImageBorder(captured);
  if (!image) return null;

  const [pageWidth, pageHeight] = CHART_PDF_PAGE;

  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: CHART_PDF_PAGE });
  const startY = drawPdfHeading(doc, { boardName, widgetName, filters, logo });

  const contentWidth = pageWidth - PDF_MARGIN * 2;
  const contentHeight = pageHeight - startY - CHART_PDF_FOOTER;

  /* ── HOW THE CHART IS FITTED ──
     The largest it can be drawn without distorting it or running off the page, for every
     chart alike.

     Filling BOTH axes was tried, for cartesian charts only, on the grounds that a line or
     bar chart's proportions are a presentation choice rather than part of its meaning:

       const drawWidth  = image.width  * (preserveAspect ? uniform : widthScale);
       const drawHeight = image.height * (preserveAspect ? uniform : heightScale);

     Twice, in fact - the second time with the picture rendered by ApexCharts itself at
     scale 2 rather than photographed, so that softness was no longer the objection. It
     still is not worth it: a widget three or four times wider than it is tall needs that
     much vertical stretch to fill an A4 landscape page, and at that point the axis labels
     are visibly drawn out. Capping the stretch only trades one compromise for another.

     This scales UP as well as down - a chart smaller than the page is enlarged to fill
     it. */
  const scale = Math.min(contentWidth / image.width, contentHeight / image.height);
  const drawWidth = image.width * scale;
  const drawHeight = image.height * scale;

  // centred both ways, so whichever axis has the leftover is balanced rather than pooling
  // at the bottom or down one side
  const x = (pageWidth - drawWidth) / 2;
  const y = startY + (contentHeight - drawHeight) / 2;

  doc.addImage(image.dataUrl, "PNG", x, y, drawWidth, drawHeight);
  drawPdfFooter(doc, pageHeight - CHART_PDF_FOOTER, { generatedBy });

  return { doc, fileName: `${widgetName || boardName || "chart"}_${stamp()}.pdf` };
};

export const exportNodeToPdf = async (context) => {
  const built = await buildNodePdf(context);
  if (!built) return false;

  built.doc.save(built.fileName);
  return true;
};

/**
 * A chart's PDF, base64-encoded, for the Email dialog to attach.
 *
 * The chart counterpart of buildRowsPdfAttachment, so a dashboard holding a table and a
 * chart sends ONE email carrying TWO PDFs - one per widget - rather than emailing only
 * the table, which is what happened while charts had no builder of their own.
 *
 * Returns null when the chart has not rendered yet or could not be photographed; the
 * caller drops it from the list rather than attaching a blank page.
 */
export const buildNodePdfAttachment = async (context) => {
  const built = await buildNodePdf(context);
  if (!built) return null;

  const dataUri = built.doc.output("datauristring");
  const marker = "base64,";
  const at = dataUri.indexOf(marker);
  if (at === -1) return null;

  return {
    fileName: built.fileName,
    fileType: "pdf",
    fileContent: dataUri.slice(at + marker.length),
  };
};

/* ─────────────────────────────────────────────────────────────
   PNG
   ───────────────────────────────────────────────────────────── */

/* ── WHY THE TABLE'S PICTURE IS DRAWN, NOT PHOTOGRAPHED ──
   The first version of this built a standalone HTML table off-screen and handed it to
   dom-to-image. It produced files Windows Photos would not open, after a long wait, and
   for two reasons that both come with photographing a DOM node:

   1. dom-to-image clones the node into an SVG foreignObject, and to make the clone look
      right it walks EVERY stylesheet in the document and inlines every web font it finds.
      This app links Material Icons from fonts.googleapis.com, so on a machine where that
      request is slow or blocked the export sat waiting on the network - the "long time" -
      for fonts our table never used.
   2. A table of a few hundred rows is taller than a canvas is allowed to be (browsers cap
      a canvas at around 16k-32k pixels a side, and area too). Over that limit the canvas
      silently fails to paint and toDataURL hands back a stub, which downloadjs writes out
      as a .png that is not a PNG. Nothing throws anywhere along that path.

   So the table is drawn straight onto a canvas here instead: no clone, no stylesheets, no
   fonts to fetch, and the size is known BEFORE anything is rendered, so a table too big to
   be an image is refused with a reason instead of saved as a broken file.

   (A chart is still photographed - it is already a picture, and a small one. See
   exportNodeToPng.) */

/* Chrome, Edge and Firefox all paint a canvas up to 16,384 pixels a side; this stays just
   inside that. A band that would exceed it is not drawn - the rows are divided into more
   bands instead. */
const CANVAS_MAX_PX = 16000;

const TABLE_PNG = {
  pad: 24,
  rowHeight: 26,
  headerHeight: 32,
  // the period banner above the header row, on a comparison export only
  bannerHeight: 28,
  cellPad: 10,
  minCol: 60,
  maxCol: 280,
  font: '12px "Inter", system-ui, -apple-system, sans-serif',
  headFont: 'bold 12px "Inter", system-ui, -apple-system, sans-serif',
  /* The heading's three tiers, in pixels, mirroring the PDF's points - see
     PDF_TITLE_SIZE. The name is the first thing anyone looks for on a file they have
     been sent, so it is the largest thing on the page. */
  titleFont: 'bold 24px "Inter", system-ui, -apple-system, sans-serif',
  subtitleFont: '16px "Inter", system-ui, -apple-system, sans-serif',
  // bold, matching the PDF's 11pt bold - see PDF_META_SIZE for why these are not small print
  metaFont: 'bold 13px "Inter", system-ui, -apple-system, sans-serif',
};

const PNG_TITLE_COLOR = "rgb(36, 90, 135)"; // the blue the mark is drawn in
const PNG_SUBTITLE_COLOR = "#475569";

/* The heading's geometry, in one place: drawPngHeading paints to these numbers, and
   drawTablePage and rowsPerPage reserve space by them. Split up, a taller title quietly
   overlapped the first row. */
const PNG_HEADING = { titleLine: 30, subtitleLine: 21, lineHeight: 20, metaGap: 10 };
// the filter lines' colour, mirroring the PDF's PDF_META_RGB
const PNG_META_COLOR = "#334155";

/* The floor the WITMEG mark needs. A one-line heading is ~34px tall and the mark is
   roughly 94, so without this the first table row is painted over it.

   Unconditional rather than "only when there is a logo": rowsPerPage and drawTablePage
   both size themselves from this number, and a figure that changed depending on whether
   an image happened to load would make the page break move with it. Reserving the space
   always costs a little white on a heading that has no mark, which nobody will see. */
const PNG_LOGO_MIN_BLOCK = 94;

/* The band's height. Takes the subtitle as well as the line count because a dashboard
   widget's name adds a whole line - reserving for it only sometimes would move the page
   break between a report and a dashboard drawn from the same data. */
const headingHeight = ({ lineCount = 0, hasSubtitle = false } = {}) =>
  Math.max(
    PNG_LOGO_MIN_BLOCK,
    PNG_HEADING.titleLine +
      (hasSubtitle ? PNG_HEADING.subtitleLine : 0) +
      // the air between the names and the filters, reserved only when there are any
      (lineCount ? PNG_HEADING.metaGap : 0) +
      lineCount * PNG_HEADING.lineHeight +
      4,
  );

/* Column widths for the picture: the author's where they set one, so the image has the
   same proportions as the table and as the PDF.

   Where a column has none, one pass over the data sizes it to its widest value, capped so
   one long description cannot push the rest off the picture. */
const measureColumns = (ctx, headers, body, widths) =>
  headers.map((header, i) => {
    const saved = widths?.[i];
    if (Number.isFinite(saved) && saved > 0) return Math.ceil(saved);

    ctx.font = TABLE_PNG.headFont;
    let width = ctx.measureText(String(header)).width;
    ctx.font = TABLE_PNG.font;
    body.forEach((row) => {
      const cell = ctx.measureText(String(row[i] ?? "")).width;
      if (cell > width) width = cell;
    });
    return Math.min(TABLE_PNG.maxCol, Math.max(TABLE_PNG.minCol, Math.ceil(width) + TABLE_PNG.cellPad * 2));
  });

const clipText = (ctx, text, maxWidth) => {
  const value = String(text ?? "");
  if (ctx.measureText(value).width <= maxWidth) return value;
  let clipped = value;
  while (clipped.length > 1 && ctx.measureText(`${clipped}…`).width > maxWidth) {
    clipped = clipped.slice(0, -1);
  }
  return `${clipped}…`;
};

/* The naming block every picture carries, table or chart: the widget's title, and under
   it the board it belongs to and the filters in force. An image that leaves the app with
   no name on it cannot be identified again once it is in someone's inbox. */
const drawPngHeading = (ctx, { title, subtitle, lines, x, y, logo, blockWidth }) => {
  ctx.textBaseline = "middle";

  /* The band's height comes from headingHeight, the same function drawTablePage and
     rowsPerPage reserve space with - so the space set aside and the space painted into
     cannot disagree. The mark and the text are both centred inside it, matching the
     PDF; see drawPdfHeading for why they are not top-aligned. */
  const blockHeight = headingHeight({ lineCount: lines.length, hasSubtitle: Boolean(subtitle) });

  let textWidth = blockWidth;
  if (logo && blockWidth) {
    const logoWidth = Math.min(LOGO_WIDTH_PX, blockWidth * LOGO_MAX_SHARE);
    const logoHeight = logoHeightFor(logo, logoWidth);
    ctx.drawImage(
      logo,
      x + blockWidth - logoWidth,
      y + (blockHeight - logoHeight) / 2,
      logoWidth,
      logoHeight,
    );
    // 24px of air, so a long report name stops short of the mark instead of touching it
    textWidth = Math.max(80, blockWidth - logoWidth - 24);
  }

  const textHeight =
    PNG_HEADING.titleLine +
    (subtitle ? PNG_HEADING.subtitleLine : 0) +
    (lines.length ? PNG_HEADING.metaGap : 0) +
    lines.length * PNG_HEADING.lineHeight;

  /* textBaseline is "middle", so every line is positioned by its own centre - hence the
     half-line offsets as this walks down the block. */
  const top = y + (blockHeight - textHeight) / 2;
  let middle = top + PNG_HEADING.titleLine / 2;

  ctx.fillStyle = PNG_TITLE_COLOR;
  ctx.font = TABLE_PNG.titleFont;
  // clipped, not wrapped: the band is a fixed height and a second line of title would be
  // drawn over the first row of the table
  ctx.fillText(clipText(ctx, String(title), textWidth), x, middle);

  if (subtitle) {
    middle += PNG_HEADING.titleLine / 2 + PNG_HEADING.subtitleLine / 2;
    ctx.font = TABLE_PNG.subtitleFont;
    ctx.fillStyle = PNG_SUBTITLE_COLOR;
    ctx.fillText(clipText(ctx, String(subtitle), textWidth), x, middle);
  }

  const lastBlock = subtitle ? PNG_HEADING.subtitleLine : PNG_HEADING.titleLine;
  ctx.font = TABLE_PNG.metaFont;
  ctx.fillStyle = PNG_META_COLOR;
  lines.forEach(([label, value], i) => {
    const text = `${label ? `${label}: ` : ""}${pdfText(value)}`;
    ctx.fillText(
      clipText(ctx, text, textWidth),
      x,
      // metaGap, the same one headingHeight reserved, opens below the names
      middle + lastBlock / 2 + PNG_HEADING.metaGap + (i + 0.5) * PNG_HEADING.lineHeight,
    );
  });
};

/* ONE image of ONE band of rows. Nearly every table is a single band - this is only
   called more than once when the rows genuinely cannot fit in one picture (see
   exportRowsToPng). It draws what it is handed, so a band out of four looks exactly like
   a table that fitted whole, apart from the "Rows 1-450 of 1,800" line. */
const drawTablePage = ({ headers, body, columns, title, subtitle, headingLines, pageLabel, logo, bands }) => {
  const { pad, rowHeight, headerHeight, bannerHeight, cellPad } = TABLE_PNG;

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const lines = pageLabel ? [...headingLines, ["", pageLabel]] : headingLines;
  const titleBlock = headingHeight({ lineCount: lines.length, hasSubtitle: Boolean(subtitle) });
  const footerBlock = 26;
  // the period banner, where there is one, sits above the header row and adds its height
  const bannerBlock = bands ? bannerHeight : 0;
  const width = pad * 2 + columns.reduce((sum, w) => sum + w, 0);
  const height = pad * 2 + titleBlock + bannerBlock + headerHeight + body.length * rowHeight + footerBlock;
  if (width > CANVAS_MAX_PX || height > CANVAS_MAX_PX) return null;

  // 2x where it fits, so the text is not soft on a high-density screen
  const scale = width * 2 <= CANVAS_MAX_PX && height * 2 <= CANVAS_MAX_PX ? 2 : 1;
  canvas.width = width * scale;
  canvas.height = height * scale;
  ctx.scale(scale, scale);

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);

  drawPngHeading(ctx, { title, subtitle, lines, x: pad, y: pad, logo, blockWidth: width - pad * 2 });

  let y = pad + titleBlock;

  /* ── THE PERIOD BANNER ──
     Drawn a shade lighter than the header beneath it so the two read as one block in
     two tiers rather than as two separate tables. Each band is centred over its own
     columns and divided from the next by a hairline, which is what tells a reader
     where one period's figures stop. */
  if (bands) {
    ctx.fillStyle = "#1e3a6d";
    ctx.fillRect(pad, y, width - pad * 2, bannerHeight);
    ctx.font = TABLE_PNG.headFont;
    ctx.textBaseline = "middle";

    let bandX = pad;
    let column = 0;
    bands.forEach(([label, span], i) => {
      const bandWidth = columns.slice(column, column + span).reduce((sum, w) => sum + w, 0);

      if (i) {
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(bandX + 0.5, y + 4);
        ctx.lineTo(bandX + 0.5, y + bannerHeight - 4);
        ctx.stroke();
      }

      if (label) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(bandX, y, bandWidth, bannerHeight);
        ctx.clip();
        ctx.fillStyle = "#ffffff";
        ctx.textAlign = "center";
        ctx.fillText(clipText(ctx, label, bandWidth - cellPad * 2), bandX + bandWidth / 2, y + bannerHeight / 2);
        ctx.restore();
      }

      bandX += bandWidth;
      column += span;
    });

    // back to the default, or every cell below would be centred on its left edge
    ctx.textAlign = "left";
    y += bannerHeight;
  }

  ctx.fillStyle = "#0f2557";
  ctx.fillRect(pad, y, width - pad * 2, headerHeight);
  ctx.fillStyle = "#ffffff";
  ctx.font = TABLE_PNG.headFont;
  let x = pad;
  headers.forEach((header, i) => {
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, columns[i], headerHeight);
    ctx.clip();
    ctx.fillText(clipText(ctx, header, columns[i] - cellPad * 2), x + cellPad, y + headerHeight / 2);
    ctx.restore();
    x += columns[i];
  });

  y += headerHeight;
  ctx.font = TABLE_PNG.font;
  body.forEach((row, r) => {
    if (r % 2) {
      ctx.fillStyle = "#f8fafc";
      ctx.fillRect(pad, y, width - pad * 2, rowHeight);
    }
    ctx.fillStyle = "#1f2937";
    x = pad;
    row.forEach((cell, i) => {
      ctx.save();
      ctx.beginPath();
      ctx.rect(x, y, columns[i], rowHeight);
      ctx.clip();
      ctx.fillText(clipText(ctx, cell, columns[i] - cellPad * 2), x + cellPad, y + rowHeight / 2);
      ctx.restore();
      x += columns[i];
    });

    ctx.strokeStyle = "#e2e8f0";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(pad, y + rowHeight - 0.5);
    ctx.lineTo(width - pad, y + rowHeight - 0.5);
    ctx.stroke();
    y += rowHeight;
  });

  ctx.font = '10px "Inter", system-ui, -apple-system, sans-serif';
  ctx.fillStyle = "#94a3b8";
  ctx.fillText(`Generated ${new Date().toLocaleString()}`, pad, y + 14);

  return canvas;
};

/* How many rows one picture can hold, worked out from the geometry rather than guessed,
   so the number moves with the row height and the heading if either changes. */
const rowsPerPage = (headingCount, hasSubtitle, hasBands) => {
  const { pad, rowHeight, headerHeight, bannerHeight } = TABLE_PNG;
  // headingCount + 1: a split export adds the "Rows 601-1,200 of 1,800" line
  const chrome =
    pad * 2 +
    headingHeight({ lineCount: headingCount + 1, hasSubtitle }) +
    (hasBands ? bannerHeight : 0) +
    headerHeight +
    26;
  return Math.max(1, Math.floor((CANVAS_MAX_PX - chrome) / rowHeight));
};

export const exportRowsToPng = async ({ rows, fields, widgetName, boardName, filters }) => {
  const matrix = buildExportMatrix({ rows, fields });
  if (!matrix) return false;

  const { headers, body } = matrix;
  const { title, subtitle, lines: headingLines } = buildHeadingLines({ boardName, widgetName, filters });

  /* A picture is the one format with a ceiling, and it is the browser's: a canvas may not
     be more than about 16k pixels a side, and beyond that it silently stops painting. So
     a table taller than one image is split across several rather than refused - the rows
     all arrive, in order, each image carrying the same headers and saying which rows it
     holds. Anything that fits, which is nearly everything, is still a single file.

     The cap is on the number of images, not the number of rows: past it someone is asking
     for a wall of pictures and wants Excel or CSV. Counted before anything is measured,
     because measuring is a pass over every cell of a table we are about to turn away. */
  // the banner repeats on every image, so it is reserved on every image
  const bands = buildPeriodBands(matrix.periods);

  const perPage = rowsPerPage(headingLines.length, Boolean(subtitle), Boolean(bands));
  const pages = Math.ceil(body.length / perPage);
  if (pages > PNG_MAX_IMAGES) return "too-many-rows";

  /* Columns are measured once, over ALL the rows, so every band lines up with the others
     - a column that is wide on image 1 is the same width on image 4. */
  const probe = document.createElement("canvas").getContext("2d");
  if (!probe) return false;
  const columns = measureColumns(probe, headers, body, matrix.widths);

  // loaded once for the whole set, so a split export does not re-fetch it per image
  const [download, logo] = await Promise.all([loadDownload(), loadLogo()]);
  const base = `${widgetName || boardName || "report"}_${timeStamp()}`;

  for (let page = 0; page < pages; page += 1) {
    const from = page * perPage;
    const slice = body.slice(from, from + perPage);
    const canvas = drawTablePage({
      headers,
      body: slice,
      columns,
      title,
      subtitle,
      headingLines,
      // only when there is more than one, so an ordinary export says nothing about pages
      pageLabel:
        pages > 1
          ? `Rows ${(from + 1).toLocaleString()}–${(from + slice.length).toLocaleString()} of ${body.length.toLocaleString()} (image ${page + 1} of ${pages})`
          : "",
      logo,
      bands,
    });

    // too wide to draw at all - splitting rows cannot help that, so say so plainly
    if (!canvas) return "too-many-rows";

    const dataUrl = canvas.toDataURL("image/png");
    if (!isUsablePng(dataUrl)) throw new Error("The image could not be rendered");
    download(dataUrl, pages > 1 ? `${base}_${page + 1}of${pages}.png` : `${base}.png`, "image/png");

    // a breath between saves: browsers throttle a burst of downloads from one gesture
    if (pages > 1) await new Promise((resolve) => setTimeout(resolve, 350));
  }

  return { files: pages };
};

/* A canvas that exceeded what the browser would paint returns a stub from toDataURL - a
   handful of characters, or a bare "data:," - and writing that out is exactly how a .png
   that will not open gets made. It is checked before it reaches the disk. */
const saveCanvas = async (canvas, fileName) => {
  const dataUrl = canvas.toDataURL("image/png");
  if (!isUsablePng(dataUrl)) throw new Error("The image could not be rendered");

  const download = await loadDownload();
  download(dataUrl, fileName, "image/png");
  return true;
};

const isUsablePng = (dataUrl) =>
  typeof dataUrl === "string" && dataUrl.startsWith("data:image/png") && dataUrl.length > 1000;

/* A chart is already a picture, and a small one, so it is photographed - there is no
   array of numbers that would reproduce the shape.

   Bounded by a timeout because dom-to-image inlines every web font the document links
   before it draws anything, and this app links one from fonts.googleapis.com: where that
   request hangs, so did the export, with a spinner and no end to it. A minute is far
   longer than a chart needs and short enough to be an answer. */
const CAPTURE_TIMEOUT_MS = 60000;

/* Captured at the node's own size, 1:1.

   Rendering at 2x was tried, to give the enlargement onto an A4 sheet more to work with.
   It did the opposite - dom-to-image takes the `style` transform on the cloned node, and
   what came back was the chart rasterised at its original size and then stretched into
   the larger canvas, so the result was softer than the plain capture, not sharper.

     const CAPTURE_SCALE = 2;
     domtoimage.toPng(node, {
         width: width * scale, height: height * scale,
         style: { transform: `scale(${scale})`, transformOrigin: "top left",
                  width: `${width}px`, height: `${height}px` },
     });

   If sharper chart PDFs are wanted, the way there is re-rendering the chart larger before
   the capture - not resampling the capture afterwards. */
const captureNode = async (node) => {
  const domtoimage = await loadDomToImage();
  const dataUrl = await Promise.race([
    domtoimage.toPng(node, {
      bgcolor: "#ffffff",
      width: node.scrollWidth || node.offsetWidth,
      height: node.scrollHeight || node.offsetHeight,
    }),
    new Promise((_resolve, reject) =>
      setTimeout(() => reject(new Error("The image took too long to render")), CAPTURE_TIMEOUT_MS),
    ),
  ]);

  if (!isUsablePng(dataUrl)) throw new Error("The image could not be rendered");
  return dataUrl;
};

export const exportNodeToPng = async ({ node, widgetName, boardName, filters }) => {
  if (!node) return false;
  const [dataUrl, download] = await Promise.all([captureNode(node), loadDownload()]);

  /* The capture on its own is a shape with no name on it - which chart, off which board,
     filtered how. The same heading a table's picture gets is drawn above it, by painting
     the capture onto a taller canvas rather than by dressing the widget up beforehand:
     nothing on the page moves, and the chart the reader is looking at is the chart in the
     file. */
  const titled = await addPngHeading(dataUrl, buildHeadingLines({ boardName, widgetName, filters }));
  download(titled || dataUrl, `${widgetName || boardName || "chart"}_${timeStamp()}.png`, "image/png");
  return true;
};

const addPngHeading = async (dataUrl, { title, subtitle, lines }) => {
  // the heading is a courtesy - a chart that will not re-load is still saved bare
  const [image, logo] = await Promise.all([loadImage(dataUrl), loadLogo()]);
  if (!image) return null;

  const { pad } = TABLE_PNG;
  const titleBlock = headingHeight({ lineCount: lines.length, hasSubtitle: Boolean(subtitle) });
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const width = image.width + pad * 2;
  const height = image.height + titleBlock + pad * 2;
  if (width > CANVAS_MAX_PX || height > CANVAS_MAX_PX) return null;

  canvas.width = width;
  canvas.height = height;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  drawPngHeading(ctx, { title, subtitle, lines, x: pad, y: pad, logo, blockWidth: width - pad * 2 });
  ctx.drawImage(image, pad, pad + titleBlock);

  const out = canvas.toDataURL("image/png");
  return isUsablePng(out) ? out : null;
};

/* Full screen for a single widget. The board-level button fullscreens the whole board
   through react-easyfullscreen; a widget has no such wrapper, so it goes straight to the
   browser API on its own element.

   The re-measure afterwards is not optional: react-grid-layout and ApexCharts only size
   themselves on mount and on window resize, and the resize that entering fullscreen
   fires lands before the new size is readable - without this the widget renders
   fullscreen at its old pixel width. */
export const toggleElementFullScreen = (element) => {
  if (!element) return;

  const current = document.fullscreenElement || document.webkitFullscreenElement;
  if (current) {
    const exit = document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen;
    if (exit) Promise.resolve(exit.call(document)).catch((err) => console.error("Fullscreen error:", err));
    return;
  }

  const request = element.requestFullscreen || element.webkitRequestFullscreen || element.msRequestFullscreen;
  if (!request) {
    console.warn("Full screen is not supported in this browser");
    return;
  }
  Promise.resolve(request.call(element))
    .then(() => setTimeout(() => window.dispatchEvent(new Event("resize")), 100))
    .catch((err) => console.error("Fullscreen error:", err));
};
